<template>
  <div class="settings">
    <v-container>
      <v-alert type="warning">
        Please be aware of the implications of changes made on this page.
      </v-alert>

      <v-row>
        <v-col cols="6">
          <v-card class="ma-1 pa-4" outlined>
            <div class="overline mb-4">GLOBAL SETTINGS</div>
            <v-select
              v-model="form.weekStartDay"
              :items="daysArray"
              label="Week starts on (day)"
              outlined
              hide-details
              class="mb-2"
            ></v-select>
            <v-text-field
              v-model="form.studyNameFilter"
              label="Study name filter"
              hint="Leave blank to see all studies"
              persistent-hint
              outlined
            ></v-text-field>
            <v-card-actions>
              <v-spacer />
              <v-btn :disabled="sending" class="warning" @click="saveForm"
                ><v-icon left>warning</v-icon> Save Settings</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-col>
        <v-col cols="6">
          <v-card class="ma-1 pa-4" outlined>
            <v-toolbar color="warning" dark>
              <v-toolbar-title>Surveys</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn icon @click="() => (showDialog = true)">
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </v-toolbar>

            <v-list>
              <v-list-group v-for="(item, key) in surveys" :key="key" no-action>
                <template v-slot:activator>
                  <v-list-item-content>
                    <v-list-item-title v-text="key"></v-list-item-title>
                  </v-list-item-content>
                </template>

                <v-list-item>
                  <v-list-item-content>
                    <v-text-field
                      v-model="surveys[key]"
                      label="URL"
                      outlined
                      :disabled="userRole !== 'admin'"
                      :append-icon="userRole === 'admin' ? 'delete' : null"
                      @click:append="removeSurvey(key)"
                    ></v-text-field>
                  </v-list-item-content>
                </v-list-item>
              </v-list-group>
            </v-list>

            <v-card-actions>
              <v-spacer />
              <v-btn
                :disabled="sending"
                class="warning"
                @click="saveSurveys"
                v-if="userRole === 'admin'"
                ><v-icon left>warning</v-icon> Save Surveys</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="6">
          <v-card class="ma-1 pa-4" outlined>
            <div class="overline mb-4">NOTIFICATIONS</div>
            <template v-for="(notif, key) in notifications">
              <v-select
                v-model="notif.value"
                :items="notificationsOptions"
                :label="notif.name"
                :placeholder="notif.name"
                multiple
                chips
                outlined
                hide-details
                :key="key"
                class="my-4"
              ></v-select>
            </template>

            <v-card-actions>
              <v-spacer />
              <v-btn
                :disabled="sending"
                class="primary"
                @click="saveNotificationsSettings"
                >Save</v-btn
              >
            </v-card-actions>
          </v-card>
        </v-col>
        <v-col cols="6"> </v-col>
      </v-row>
    </v-container>

    <v-dialog v-model="showDialog" max-width="400">
      <v-card class="pa-4">
        <v-card-title class="headline">New Survey</v-card-title>

        <v-card-text>
          <v-text-field
            v-model="newSurveyKey"
            label="Survey ID"
            hint="The survey ID must be unique (otherwise it will overwrite the current survey using this ID). The ID must not contain '/', '.', '#', '$', '[', or ']'"
            persistent-hint
            outlined
          ></v-text-field>
          <v-text-field
            v-model="newSurveyLink"
            label="URL"
            outlined
          ></v-text-field>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showDialog = false">
            Cancel
          </v-btn>
          <v-btn color="primary" @click="addSurvey">
            Add
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import firebase from "firebase/app";
import { ROLES, DAYS_ARRAY } from "../globals";

import { mapState } from "vuex";

export default {
  name: "settings",
  data: () => ({
    form: {
      studyNameFilter: null,
      weekStartDay: null
    },
    sending: false,
    showDialog: false,
    surveys: null,
    newSurveyKey: null,
    newSurveyLink: null,
    notifications: null,
    daysArray: DAYS_ARRAY
  }),
  computed: {
    ...mapState([
      "userRole",
      "dbAdminUsers",
      "studyNameFilter",
      "weekStartDay"
    ]),
    adminUser() {
      return this.$store.state.adminUser;
    },
    notificationsOptions() {
      let options = [
        { value: "designated-rn", text: "Assigned RN (Group)" },
        { value: "designated-md", text: "Assigned MD (Group)" }
      ];

      for (let [key, value] of Object.entries(this.dbAdminUsers)) {
        if (!value.deleted) {
          options.push({
            value: `admin_${key}`,
            text: `${ROLES[value.role]} - ${value.name}`
          });
        }
      }
      return options;
    }
  },
  mounted() {
    this.form.studyNameFilter = this.studyNameFilter;
    this.form.weekStartDay = this.weekStartDay;
  },
  created() {
    firebase
      .database()
      .ref("/Surveys/")
      .once("value", snapshot => (this.surveys = snapshot.val()));

    firebase
      .database()
      .ref("/AdminSettings/")
      .child("notifications")
      .once("value", snapshot => (this.notifications = snapshot.val()));
  },
  methods: {
    saveForm() {
      this.$store.dispatch("saveSettings", this.form);
    },
    addSurvey() {
      this.surveys[this.newSurveyKey] = this.newSurveyLink;

      this.newSurveyKey = null;
      this.newSurveyLink = null;
      this.showDialog = false;
    },
    saveSurveys() {
      this.$store.dispatch("saveSurveys", this.surveys);
    },
    removeSurvey(surveyId) {
      this.$delete(this.surveys, surveyId);
    },
    saveNotificationsSettings() {
      this.$store.dispatch("saveNotificationsSettings", this.notifications);
    }
  }
};
</script>
